import type { IGenericAPIResponse, ISearchUserByNameResponse } from '#api';
import type { IOnlineEventData, TFriendStatus } from '#shared/types/firebase.types';
import { useDateFormat } from '@vueuse/core';
import { useAppToast } from '~/composables/shared/useAppToast';
import { useTypedNuxtApp } from '~/composables/useTypedNuxtApp';
import { stores } from '~/store/index';

const DEFAULT_PROFILE_PICTURE = 'Profile_Pic.png';

export const useFriendsStore = defineStore('friends', () => {
  const { showErrorToast, showSuccessToast } = useAppToast();

  const friends = ref<IFriendData[]>([]);
  const onlineFriends = ref<Set<string>>(new Set());
  const blockedFriends = computed(() => filterFriends(EFriendStatus.BLOCKED));
  const acceptedFriends = computed(() => filterFriends(EFriendStatus.FRIEND));
  const rejectedFriends = computed(() => filterFriends(EFriendStatus.REJECTED));
  const sentFriends = computed(() => filterFriends(EFriendStatus.SENT));
  const receivedFriends = computed(() => filterFriends(EFriendStatus.RECEIVED));

  function filterFriends(filter: TFriendStatus) {
    return friends.value.filter((friend) => friend.status === filter);
  }

  function setFriendOnlineStatus({ displayName, isOnline }: IOnlineEventData) {
    if (isOnline) {
      onlineFriends.value.add(displayName);
    } else {
      onlineFriends.value.delete(displayName);
    }
  }

  async function getUserFriends() {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const { list } = await $authApi.post<{ list: IFriendData[] }>('/friendslist', { sessionKey });

      friends.value = list
        .map((friend: IFriendData) => ({
          ...friend,
          date: useDateFormat(friend.timestamp, 'YYYY / MM / DD').value,
        }))
        .filter((item: IFriendData) => item.status !== EFriendStatus.REMOVED);
    } catch (e) {
      console.error(e);
    }
  }

  async function searchUserByName(displayName: string, excludeUsers: string[]) {
    if (displayName.length < 4) return [];

    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const { displayNames } = await $authApi.post<ISearchUserByNameResponse>('/searchdisplayname', {
        sessionKey,
        searchString: displayName,
        excludeUsers,
      });

      return displayNames;
    } catch (error: any) {
      console.error(error);
      return [];
    }
  }

  async function sendFriendRequest(friendToAdd: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      return $authApi.post<IGenericAPIResponse>('/friendRequest', { sessionKey, friendToAdd });
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error?.data?.message || 'Failed to send friend request',
      };
    }
  }

  async function removeFriend(friendToRemoveId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const data = await $authApi.post<IGenericAPIResponse>('/removefriend', { sessionKey, friendToRemoveId });

      if (data.status === 'SUCCESS') {
        await getUserFriends();
      }

      return data;
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error.message || 'Failed to remove friend',
      };
    }
  }

  async function blockFriend(friendToBlockId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      const data = await $authApi.post<IGenericAPIResponse>('/blockfriend', { sessionKey, friendToBlockId });

      if (data.status === 'SUCCESS') {
        await getUserFriends();
      }

      return data;
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error.message || 'Failed to send friend request',
      };
    }
  }

  async function unblockFriend(user: IFriendData) {
    const { $authApi } = useTypedNuxtApp();

    const sessionKey = await stores.user.getIdToken();
    const friendToUnblockId = user.friendId;

    try {
      const response = await $authApi.post<IGenericAPIResponse>('/unblockfriend', { sessionKey, friendToUnblockId });

      if (response.status === 'SUCCESS') {
        showSuccessToast('Success', `${user.displayName} has been unblocked successfully`);
        await getUserFriends();
      }
    } catch (e) {
      console.error(e);
      showErrorToast();
    }
  }

  function appendListOfFriends(payload: IFriendData) {
    const friend = {
      ...payload,
      key: payload.friendId,
      image: DEFAULT_PROFILE_PICTURE,
    };

    const idx = friends.value.findIndex((f) => f.friendId === friend.friendId);

    if (idx === -1) {
      friends.value.push(friend);
    } else {
      friends.value.splice(idx, 1, friend);
    }
  }

  function removeFromListOfFriends(friendData: IFriendData) {
    const index = friends.value.findIndex((friend) => friend.friendId === friendData.friendId);

    if (index === -1) return;

    friends.value = friends.value.filter((_, idx) => idx !== index);
  }

  const onSocketFriendChanged = (friendData: IFriendData) => {
    const { $socket } = useTypedNuxtApp();
    appendListOfFriends(friendData);
    $socket.emit('unsubscribeToOnlineStatus', { displayNames: [friendData.displayName] });
    $socket.emit('subscribeToOnlineStatus', { onlineUsers: [friendData.displayName] });
  };

  const onSocketFriendRemoved = (friendData: IFriendData) => {
    const { $socket } = useTypedNuxtApp();
    removeFromListOfFriends(friendData);
    $socket.emit('unsubscribeToOnlineStatus', { displayNames: [friendData.displayName] });
  };

  const onSocketOnlineEvent = (data: IOnlineEventData) => {
    setFriendOnlineStatus(data);
  };

  return {
    friends,
    onlineFriends,
    blockedFriends,
    acceptedFriends,
    rejectedFriends,
    sentFriends,
    receivedFriends,

    getUserFriends,
    sendFriendRequest,
    removeFriend,
    blockFriend,
    unblockFriend,
    searchUserByName,

    onSocketFriendChanged,
    onSocketFriendRemoved,
    onSocketOnlineEvent,
  };
});
